import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  Play,
  BookOpen,
  Wind,
  Eye,
  Shield,
  Target,
  Compass,
  Heart,
  Bookmark,
  Share2,
  GraduationCap,
  Users,
  ShoppingBag,
  Landmark,
  Map as MapIcon,
  Sparkles,
} from "lucide-react";
import { AnkhMark } from "@/components/site/AnkhMark";
import { LivingFrequencyField } from "@/components/site/LivingFrequencyField";
import { GoldenThread } from "@/components/site/GoldenThread";
import { IdleQuote } from "@/components/site/IdleQuote";
import { ConsciousBreath } from "@/components/site/ConsciousBreath";
import { DailyGate } from "@/components/site/DailyGate";
import { LivingRecommendations } from "@/components/site/LivingRecommendations";
import { NextStep, JourneyLink } from "@/components/site/NextStep";
import { useJourney } from "@/hooks/use-journey";
import { secondaryActions } from "@/lib/journey";
import { SectionHeader } from "@/components/site/SectionHeader";
import { ResponsiveImage } from "@/components/site/ResponsiveImage";
import { PlaceholderArt } from "@/components/site/PlaceholderArt";
import { ReleaseCover } from "@/components/site/ReleaseCover";
import { PlayButton } from "@/components/site/PlayButton";
import { TodayActivation } from "@/components/site/TodayActivation";
import { LivingQuote } from "@/components/site/LivingQuote";
import {
  WISDOM,
  PLATFORMS,
  STREAMING_LINKS,
  type Release,
  getCurrentRelease,
  getNextRelease,
  formatReleaseDate,

} from "@/lib/content";
import { getReleases } from "@/lib/music.functions";
import { getBooks, type PublicBook } from "@/lib/books.functions";
import { getWisdomIndex } from "@/lib/wisdom.functions";
import type { PublicWisdomPost } from "@/lib/wisdom-kinds";
import { absoluteUrl, SITE_URL } from "@/lib/site";
import { track } from "@/lib/analytics";
import { subscribeEmail } from "@/lib/newsletter.functions";
import { getNews } from "@/lib/news";
import {
  PORTRAIT_ALT,
  PORTRAIT_URL,
  ldScript,
  orgRef,
  pageNode,
  personRef,
  seoMeta,
} from "@/lib/schema";


export const Route = createFileRoute("/")({
  loader: async () => {
    const [releases, books, wisdom] = await Promise.all([
      getReleases(),
      getBooks(),
      // Published editorial, newest first — the same database rows /wisdom uses.
      getWisdomIndex().catch(() => [] as PublicWisdomPost[]),
    ]);
    return { releases, books, wisdom };
  },
  component: Home,
  head: () => ({
    ...seoMeta({
      title: "Buddha KA | Artist, Author & Founder of KAfrequency",
      description:
        "Official website of Buddha KA, a Chicago-born recording artist, author, and creator of KAfrequency. Explore music, books, daily activations, visual worlds, and consciousness systems.",
      path: "/",
      image: PORTRAIT_URL,
      imageAlt: PORTRAIT_ALT,
      extraMeta: [{ property: "og:image:type", content: "image/png" }],
    }),
    links: [
      { rel: "canonical", href: SITE_URL + "/" },
      // No image preload here: the portrait is only an og:image on this route,
      // never rendered in the viewport. Preloading the 2.5 MB master cost the
      // home page its entire image budget for a file the page never paints.
    ],
    scripts: [
      ldScript(
        pageNode({
          type: "WebPage",
          id: `${SITE_URL}/#homepage`,
          url: `${SITE_URL}/`,
          name: "Buddha KA — official website",
          description:
            "Music, books, daily activations, visual worlds, and consciousness systems from Buddha KA — founder of KAfrequency.",
          image: PORTRAIT_URL,
          imageCaption: PORTRAIT_ALT,
          mainEntity: personRef(),
          about: [personRef(), orgRef()],
        }),
      ),
    ],
  }),

});

function Home() {
  useEffect(() => { track("home_view"); }, []);
  return (
    <>
      <LivingFrequencyField />
      <GoldenThread />
      <IdleQuote />
      {/* LISTEN — the dominant first offer. */}
      <Hero />
      {/* ENTER — one clear invitation set, then the daily practice. */}
      <ConversionPaths />
      <DailyGate />
      <DailyActivationSection />
      <KaIntelligenceSection />
      {/* Proof of depth, in journey order. */}
      <NewestMusic />
      <WisdomFeed />
      <NextStep kind="home" heading="Your next step" />
      {/* OWN — offered after value, never before it. */}
      <BooksSection />
      <CoursesSection />
      <LivingRecommendations />
      {/* Depth is preserved but staged, so the first screens stay decisive. */}
      <EcosystemDepth />
      <NewsletterSection />
    </>
  );
}

/* Progressive disclosure for the lower ecosystem. Nothing is removed — the
   full map, inner technologies, community, merch, and news stay one tap away
   for visitors who want the whole world. */
/* ────────────────── KA Intelligence ────────────────── */
function KaIntelligenceSection() {
  return (
    <section
      id="ka-intelligence"
      aria-labelledby="ka-intelligence-heading"
      data-reveal
      className="border-y hairline bg-temple py-20 sm:py-28"
    >
      <div className="mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 lg:grid-cols-[1.15fr_1fr] lg:items-center">
        <div>
          <p className="text-[10px] uppercase tracking-[0.35em] text-gold">Inner technology · Live</p>
          <h2 id="ka-intelligence-heading" className="mt-4 font-serif text-4xl sm:text-5xl">
            KA Intelligence
          </h2>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            A studied conversation with the KAfrequency canon. Ask about the music, the books,
            the 47 Gates, or the practice itself — and receive answers grounded in the published
            work, with sources shown. Choose the depth you want, from a quick guide to a longer,
            more contemplative response.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-foreground/80">
            <li className="flex items-start gap-2"><Sparkles size={14} className="mt-1 shrink-0 text-gold" aria-hidden /> Answers cite the canon they come from.</li>
            <li className="flex items-start gap-2"><Compass size={14} className="mt-1 shrink-0 text-gold" aria-hidden /> Selectable depth: Guide, Scholar, or Deep Temple.</li>
            <li className="flex items-start gap-2"><Shield size={14} className="mt-1 shrink-0 text-gold" aria-hidden /> Private by default. Personal memory is opt-in.</li>
          </ul>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link
              to="/intelligence"
              search={{ from: undefined }}
              className="inline-flex items-center gap-2 rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian shadow-gold"
            >
              Enter KA Intelligence <ArrowRight size={14} aria-hidden />
            </Link>
            <span className="text-xs text-muted-foreground">Free to begin. No account required.</span>
          </div>
        </div>
        <div className="rounded-2xl border hairline bg-obsidian/60 p-6">
          <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Ask, for example</p>
          <ul className="mt-4 space-y-3 text-sm text-foreground/85">
            <li className="rounded-xl border hairline px-4 py-3">“Who is Buddha KA?”</li>
            <li className="rounded-xl border hairline px-4 py-3">“What is The Diamond Sutra about?”</li>
            <li className="rounded-xl border hairline px-4 py-3">“Where do I start with the 47 Gates?”</li>
          </ul>
          <p className="mt-5 text-xs text-muted-foreground">
            It is a study companion, not an oracle — it can be wrong, and it will tell you when
            something is not in the canon.
          </p>
        </div>
      </div>
    </section>
  );
}

function EcosystemDepth() {
  return (
    <section aria-labelledby="ecosystem-depth" className="border-t hairline">
      <details className="group mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 rounded-2xl border hairline bg-obsidian/50 px-6 py-5 transition hover:border-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold">
          <span>
            <span className="text-[10px] uppercase tracking-[0.35em] text-gold">The whole ecosystem</span>
            <span id="ecosystem-depth" className="mt-2 block font-serif text-2xl text-ivory">
              Explore the full map
            </span>
            <span className="mt-1 block text-sm text-muted-foreground">
              The Life Journey Map, inner technologies, community, merch, and the newsroom.
            </span>
          </span>
          <ArrowRight
            size={18}
            aria-hidden
            className="shrink-0 text-gold transition-transform group-open:rotate-90"
          />
        </summary>
        <div className="mt-4">
          <JourneySection />
          <InnerTechnologies />
          <CommunitySection />
          <MerchSection />
          <LatestNewsSection />
        </div>
      </details>
    </section>
  );
}

/* ────────────────── Four paths in · one membership ────────────────── */
const PATHS = [
  { key: "listen", to: "/music", eyebrow: "Listen", title: "The Music", body: "Frequency-built songs, albums, and unreleased transmissions." },
  { key: "learn", to: "/academy", eyebrow: "Learn", title: "The Academy", body: "University-depth lessons across fourteen schools of consciousness." },
  { key: "activate", to: "/activations", eyebrow: "Activate", title: "Daily Activations", body: "A practice for every day — breath, intention, instruction, journal." },
] as const;

function ConversionPaths() {
  return (
    <section aria-labelledby="paths-heading" className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
      <h2 id="paths-heading" className="font-serif text-3xl sm:text-4xl">Choose your way in.</h2>
      <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
        Listen, learn, or activate — free to begin. One membership deepens all three.
      </p>

      <div className="mt-8 grid gap-4 md:grid-cols-4">
        {PATHS.map((p) => (
          <Link
            key={p.key}
            to={p.to}
            onClick={() => track("home_path_click", { path: p.key })}
            className="group rounded-2xl border hairline bg-charcoal p-6 transition hover:border-gold"
          >
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{p.eyebrow}</p>
            <h3 className="mt-3 font-serif text-2xl">{p.title}</h3>
            <p className="mt-2 text-sm text-muted-foreground">{p.body}</p>
            <span className="mt-4 inline-flex items-center gap-1 text-xs text-gold">
              Explore <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" aria-hidden />
            </span>
          </Link>
        ))}
        <Link
          to="/membership"
          onClick={() => track("home_path_click", { path: "join" })}
          className="group rounded-2xl border border-gold bg-gold/5 p-6 shadow-gold transition hover:bg-gold/10"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Join</p>
          <h3 className="mt-3 font-serif text-2xl text-gold-luminous">KAfrequency Membership</h3>
          <p className="mt-2 text-sm text-foreground/75">
            Every daily activation, the full Academy canon, early music, and members-only teachings.
          </p>
          <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-gold">
            See membership <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" aria-hidden />
          </span>
        </Link>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border hairline bg-black/20 p-5">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Free account</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Dashboard, library, wishlist, free music, public teachings, and beginner Academy lessons.
          </p>
        </div>
        <div className="rounded-xl border border-gold/40 bg-gold/5 p-5">
          <p className="text-xs uppercase tracking-[0.2em] text-gold">Membership</p>
          <p className="mt-2 text-sm text-foreground/80">
            Everything free, plus daily activations, master-tier lessons, certifications, early releases, and the private community.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ────────────────── The Journey Through the 47 Gates ────────────────── */
function JourneySection() {
  return (
    <section
      id="journey"
      data-reveal
      className="relative overflow-hidden border-y hairline bg-obsidian py-20 sm:py-28"
    >
      <div
        aria-hidden
        className="absolute -left-32 top-1/2 -z-0 h-[520px] w-[520px] -translate-y-1/2 rounded-full bg-gold/5 blur-3xl"
      />
      <div
        aria-hidden
        className="absolute -right-32 top-1/3 -z-0 h-[420px] w-[420px] rounded-full bg-emerald-500/5 blur-3xl"
      />

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeader
          eyebrow="A Signature Experience"
          title="The Journey Through the 47 Gates."
          description="Not a game. A living practice. Walk each Gate through reflection, breath, sound, and journal — and watch the Temple of Consciousness illuminate around you."
        />

        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          <FeatureTile
            icon={Compass}
            eyebrow="Begin"
            title="Walk the 47 Gates"
            body="Each Gate offers a reflection, a breath practice, a creative prompt, and a journal invitation."
            to="/gates"
            cta="Enter the Gates"
          />
          <FeatureTile
            icon={MapIcon}
            eyebrow="Trace"
            title="Your Life Journey Map"
            body="A living constellation. Walked Gates glow gold. Your current Gate breathes with you."
            to="/gates/map"
            cta="View your map"
          />
          <FeatureTile
            icon={Landmark}
            eyebrow="Inhabit"
            title="The Temple of Consciousness"
            body="A sanctuary that grows as your practice deepens. Lantern, hall, library, chamber, sanctum."
            to="/journey/temple"
            cta="Enter the Temple"
          />
        </div>

        <div className="mt-12 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <Link
            to="/gates/$n"
            params={{ n: "1" }}
            onClick={() => track("hero_cta_click", { cta: "begin_journey_gate_1" })}
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold-gradient px-7 py-3.5 text-sm font-semibold text-obsidian shadow-gold transition-all hover:shadow-halo"
          >
            <Sparkles size={14} />
            Begin at the First Gate
            <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/gates"
            onClick={() => track("hero_cta_click", { cta: "browse_all_gates" })}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-gold px-7 py-3.5 text-sm text-gold hover:bg-gold hover:text-obsidian"
          >
            See all 47 Gates
          </Link>
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          No leaderboards. No streaks. No currency. A quiet practice, at your own pace.
        </p>
      </div>
    </section>
  );
}

function FeatureTile({
  icon: Icon,
  eyebrow,
  title,
  body,
  to,
  cta,
}: {
  icon: typeof Landmark;
  eyebrow: string;
  title: string;
  body: string;
  to: "/gates" | "/gates/map" | "/journey/temple";
  cta: string;
}) {
  return (
    <Link
      to={to}
      className="group relative flex flex-col rounded-2xl border hairline bg-obsidian/60 p-6 backdrop-blur transition-all hover:border-gold hover:bg-obsidian/80 sm:p-8"
    >
      <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 bg-gold/5 text-gold transition-colors group-hover:border-gold group-hover:bg-gold/10">
        <Icon size={18} />
      </div>
      <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{eyebrow}</p>
      <h3 className="mt-2 font-serif text-xl text-ivory group-hover:text-gold-luminous sm:text-2xl">
        {title}
      </h3>
      <p className="mt-3 text-sm text-muted-foreground">{body}</p>
      <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-gold-luminous">
        {cta}
        <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
      </span>
    </Link>
  );
}

/* ─────────────── Rotating Invitation ─────────────── */
const INVITATIONS = [
  "The journey begins with attention.",
  "Every path begins with awareness.",
  "Welcome to The First Gate.",
];

function RotatingInvitation() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const t = window.setInterval(() => setI((n) => (n + 1) % INVITATIONS.length), 9000);
    return () => window.clearInterval(t);
  }, []);
  return (
    <p
      aria-live="polite"
      className="mt-4 min-h-[1.5em] font-serif text-sm italic tracking-wide text-gold-luminous/85 transition-opacity duration-[1200ms] sm:text-base"
      key={i}
    >
      {INVITATIONS[i]}
    </p>
  );
}

/* ─────────────────────────── 1 · HERO ─────────────────────────── */
function Hero() {
  // Data-driven release prominence: the next dated release is promoted until
  // its release date passes, after which the newest live release takes over.
  const { releases } = Route.useLoaderData() as { releases: Release[] };
  const nextRelease = getNextRelease(releases);
  const currentRelease = getCurrentRelease(releases);
  const spotlight = nextRelease ?? currentRelease;
  const spotlightDate = spotlight ? formatReleaseDate(spotlight) : null;
  return (

    <section className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10 halo animate-halo" aria-hidden />
      <div
        className="absolute left-1/2 top-1/2 -z-10 h-[480px] w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-full border hairline opacity-30 sm:h-[680px] sm:w-[680px]"
        aria-hidden
      />
      <div
        className="absolute left-1/2 top-1/2 -z-10 h-[720px] w-[720px] -translate-x-1/2 -translate-y-1/2 rounded-full border hairline opacity-15 sm:h-[980px] sm:w-[980px]"
        aria-hidden
      />

      <div className="mx-auto flex min-h-[88vh] max-w-4xl flex-col items-center justify-center px-4 py-24 text-center sm:px-6 sm:py-32">
        <div className="animate-rise">
          <div className="mb-10 flex items-center justify-center">
            <div className="relative">
              <div
                className="absolute inset-0 -m-8 rounded-full halo animate-halo opacity-80"
                aria-hidden
              />
              <button
                type="button"
                aria-label="Activate the KAfrequency field"
                onClick={(e) => {
                  const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                  const cx = rect.left + rect.width / 2;
                  const cy = rect.top + rect.height / 2;
                  const key = "kaf:ankh-signature-fired";
                  const already =
                    typeof window !== "undefined" &&
                    window.sessionStorage.getItem(key) === "1";
                  if (!already) {
                    try { window.sessionStorage.setItem(key, "1"); } catch { /* ignore */ }
                    window.dispatchEvent(
                      new CustomEvent("kaf:ankh-signature", { detail: { x: cx, y: cy } }),
                    );
                    track("hero_signature_activation", {});
                  } else {
                    // Subsequent taps still emit a gentle bloom.
                    window.dispatchEvent(
                      new CustomEvent("kaf:surge", { detail: { x: cx, y: cy } }),
                    );
                  }
                }}
                className="relative rounded-full transition-transform duration-300 hover:scale-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
              >
                <AnkhMark size={128} animated className="relative drop-shadow-[0_0_40px_rgba(212,175,55,0.35)] sm:!h-40 sm:!w-40" />
              </button>
            </div>
          </div>

          <p className="text-[11px] uppercase tracking-[0.4em] text-gold sm:text-xs">
            KAfrequency · A Living Ecosystem
          </p>

          <h1 className="mt-6 font-serif text-4xl leading-[1.05] tracking-tight sm:text-6xl md:text-7xl">
            <span className="block text-ivory">Buddha KA</span>
            <span className="mt-2 block bg-gold-gradient bg-clip-text text-transparent">
              Music, Wisdom &amp; Inner Technology
            </span>
          </h1>

          <p className="mt-4 font-serif text-lg italic text-gold-luminous/85 sm:text-xl">
            Train the Mind. Govern the Current.
          </p>

          <RotatingInvitation />

          <p className="mx-auto mt-6 max-w-2xl text-base text-muted-foreground sm:text-lg">
            Buddha KA (Curtis Shores) is a Chicago-born independent recording artist, author, and founder of
            KAfrequency — an artist-owned ecosystem uniting music, books, daily activations, visual worlds, and
            consciousness systems.
            {spotlight ? (
              <>
                {" "}
                {nextRelease ? "Next release" : "Out now"}:{" "}
                <Link
                  to="/music/$slug"
                  params={{ slug: spotlight.slug }}
                  onClick={() => track("next_release_cta_click", { placement: "hero_prose" })}
                  className="text-ivory underline decoration-gold/50 underline-offset-4 hover:text-gold-luminous"
                >
                  {spotlight.title}
                </Link>
                {spotlight.releaseDate && spotlightDate ? (
                  <>
                    {" "}· <time dateTime={spotlight.releaseDate}>{spotlightDate}</time>
                  </>
                ) : null}
                .
              </>
            ) : null}
          </p>

          {spotlight && (
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/music/$slug"
                params={{ slug: spotlight.slug }}
                onClick={() => track("next_release_cta_click", { placement: "hero_primary" })}
                className="group inline-flex min-h-12 w-full max-w-sm items-center justify-center gap-2 rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian shadow-gold sm:w-auto"
              >
                {nextRelease ? `Pre-save ${spotlight.title}` : `Listen to ${spotlight.title}`}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          )}




          <nav aria-label="Explore Buddha KA" className="mx-auto mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs uppercase tracking-[0.25em] text-gold">
            <Link to="/about" className="hover:text-gold-luminous">About</Link>
            <span aria-hidden className="opacity-40">·</span>
            <Link to="/artist/buddha-ka" className="hover:text-gold-luminous">Artist</Link>
            <span aria-hidden className="opacity-40">·</span>
            <Link to="/music" className="hover:text-gold-luminous">Music</Link>

            <span aria-hidden className="opacity-40">·</span>
            <Link to="/press" className="hover:text-gold-luminous">Press</Link>
            <span aria-hidden className="opacity-40">·</span>
            <Link to="/news" className="hover:text-gold-luminous">News</Link>
          </nav>

          {/* Phase 3A — one journey-aware secondary row. The dominant CTA above
              is the release (LISTEN); these never compete with it. */}
          <HeroJourneyCTAs />


          <ConsciousBreath />
        </div>

        {/* Scroll hint */}
        <div className="pointer-events-none mt-16 flex flex-col items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-muted-foreground">
          <span>Descend</span>
          <span className="h-8 w-px bg-gradient-to-b from-gold/70 to-transparent" aria-hidden />
        </div>
      </div>
    </section>
  );
}

/* Journey-aware secondary CTAs. At most two, never duplicating the release
   primary, and never asking a member to join or a signed-in visitor to
   register. The row reserves its height so resolving state causes no shift. */
function HeroJourneyCTAs() {
  const { visitor } = useJourney();
  const actions = secondaryActions(visitor);
  return (
    <div className="mt-10 flex min-h-[52px] flex-col items-center justify-center gap-3 sm:flex-row">
      {actions.map((a) => (
        <JourneyLink key={a.id} action={a} surface="home_hero" className="w-full sm:w-auto" />
      ))}
      {/* Phase 3B — the shortest path to a first real experience. */}
      <Link
        to="/start"
        onClick={() => track("journey_secondary_cta", { action: "start_here", surface: "home_hero" })}
        className="text-sm text-muted-foreground underline decoration-gold/40 underline-offset-4 transition hover:text-gold-luminous"
      >
        New here? Start here.
      </Link>
    </div>
  );
}


/* ────────────────────── 2 · DAILY ACTIVATION ────────────────────── */
function DailyActivationSection() {
  return (
    <section id="activation" className="border-y hairline bg-temple py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeader
          eyebrow="Section 01 · Daily Activation"
          title="The hour is asking for you."
          description="A time-attuned practice with audio, breath, and a chain of consecutive days. Sign in to keep the streak."
        />
        <div className="mt-10">
          <TodayActivation />
        </div>
        <div className="mt-12">
          <LivingQuote />
        </div>
      </div>
    </section>
  );
}

/* ────────────────────── 3 · NEWEST MUSIC ────────────────────── */
function NewestMusic() {
  const { releases } = Route.useLoaderData() as { releases: Release[] };
  const upcoming = getNextRelease(releases);
  const featured =
    getCurrentRelease(releases) ??
    releases.find((r) => r.status === "released") ??
    releases[0];

  if (!featured) return null;

  const alt = releases.filter((r) => r.slug !== featured.slug && r.slug !== upcoming?.slug).slice(0, 3);


  const platforms: Array<{ name: string; url?: string }> = [
    { name: "Spotify", url: STREAMING_LINKS["Spotify"] },
    { name: "Apple Music", url: STREAMING_LINKS["Apple Music"] ?? featured.links?.["Apple Music"] },
    { name: "YouTube", url: STREAMING_LINKS["YouTube"] },
    { name: "Bandcamp", url: STREAMING_LINKS["Bandcamp"] },
  ];

  return (
    <section id="music" data-reveal className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28">
      <SectionHeader
        eyebrow="Section 02 · Newest Music"
        title="Sound engineered as instruction."
        description="Play a preview. Then follow the frequency to your platform of choice."
      />

      {upcoming && (
        <Link
          to="/music/$slug"
          params={{ slug: upcoming.slug }}
          onClick={() => track("next_release_cta_click", { placement: "music_section" })}
          className="group mt-10 flex flex-col gap-2 rounded-2xl border border-gold/40 bg-temple p-5 transition-colors hover:border-gold sm:flex-row sm:items-center sm:justify-between sm:p-6"
        >
          <span>
            <span className="block text-[11px] uppercase tracking-[0.3em] text-gold">Next release</span>
            <span className="mt-1 block font-serif text-2xl text-ivory group-hover:text-gold-luminous">
              {upcoming.title}
            </span>
          </span>
          <span className="inline-flex items-center gap-2 text-sm text-gold-luminous">
            Pre-save
            {upcoming.releaseDate ? (
              <>
                {" · "}
                <time dateTime={upcoming.releaseDate}>{formatReleaseDate(upcoming)}</time>
              </>
            ) : null}
            <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
          </span>

        </Link>
      )}


      <div className="mt-12 grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
        <ReleaseCover
          coverUrl={featured.coverUrl}
          label={`${featured.title} cover art`}
          alt={`${featured.title} cover art`}
          className="shadow-gold"
        />

        <div className="flex flex-col justify-center">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">
            {featured.type}
            {featured.year ? ` · ${featured.year}` : ""}
          </p>
          <h3 className="mt-3 font-serif text-4xl sm:text-5xl">{featured.title}</h3>
          <p className="mt-5 max-w-xl font-serif text-lg italic text-ivory/90">
            &ldquo;{featured.statement}&rdquo;
          </p>

          {featured.previewUrl ? (
            <div className="mt-8">
              <PlayButton
                track={{
                  id: featured.slug,
                  title: `${featured.title} · Preview`,
                  subtitle: "Buddha KA",
                  audioUrl: featured.previewUrl,
                  coverUrl: featured.coverUrl,
                  href: `/music/${featured.slug}`,
                }}
                label="Play preview"
              />
            </div>
          ) : null}

          <div className="mt-8 grid grid-cols-2 gap-2 sm:grid-cols-4" aria-label="Streaming destinations">
            {platforms
              .filter((p): p is { name: string; url: string } => Boolean(p.url))
              .map(({ name, url }) => (
                <a
                  key={name}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Listen to ${featured.title} on ${name}`}
                  onClick={() => track("streaming_click", { platform: name, slug: featured.slug })}
                  className="inline-flex items-center justify-center rounded-full border border-gold px-3 py-2.5 text-xs text-gold hover:bg-gold hover:text-obsidian"
                >
                  {name}
                </a>
              ))}
          </div>

          <Link
            to="/music/$slug"
            params={{ slug: featured.slug }}
            className="mt-8 inline-flex items-center gap-2 self-start text-sm font-medium text-gold-luminous hover:text-gold"
          >
            Enter the release world <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      {/* Adjacent releases */}
      <div className="mt-16 grid gap-6 sm:grid-cols-3">
        {alt.map((r: Release) => (
          <Link
            key={r.slug}
            to="/music/$slug"
            params={{ slug: r.slug }}
            className="group"
          >
            <ReleaseCover coverUrl={r.coverUrl} label={`${r.title} cover`} alt={`${r.title} cover`} />
            <p className="mt-3 font-serif text-lg group-hover:text-gold-luminous">{r.title}</p>
            <p className="text-xs text-muted-foreground">
              {r.type}
              {r.year ? ` · ${r.year}` : ""}
            </p>
          </Link>
        ))}
      </div>

      <div className="mt-10 flex justify-center">
        <Link
          to="/music"
          className="inline-flex items-center gap-2 rounded-full border border-gold px-6 py-3 text-sm text-gold hover:bg-gold hover:text-obsidian"
        >
          All music <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
}

/* ────────────────────── 4 · WISDOM FEED ────────────────────── */
function WisdomFeed() {
  const [visible, setVisible] = useState(4);
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const { wisdom } = Route.useLoaderData() as { wisdom: PublicWisdomPost[] };
  // Newest published transmissions come from the database; the repository
  // canon is the fallback if the reading room is momentarily unavailable.
  const source = wisdom.length ? wisdom : WISDOM;
  const items = useMemo(() => source.slice(0, visible), [source, visible]);
  const canLoadMore = visible < source.length;

  function toggleLike(slug: string) {
    setLiked((p) => ({ ...p, [slug]: !p[slug] }));
    track("wisdom_like", { slug });
  }
  function toggleSave(slug: string) {
    setSaved((p) => ({ ...p, [slug]: !p[slug] }));
    track("wisdom_save", { slug });
  }
  async function share(slug: string, title: string) {
    const url = absoluteUrl(`/wisdom/${slug}`);
    track("wisdom_share", { slug });
    try {
      const nav = typeof navigator !== "undefined" ? navigator : null;
      if (nav && typeof nav.share === "function") {
        await nav.share({ title, url });
        return;
      }
      if (nav && nav.clipboard) {
        await nav.clipboard.writeText(url);
      }
    } catch {
      /* user cancelled share */
    }
  }

  return (
    <section id="wisdom" data-reveal className="border-y hairline bg-charcoal py-20 sm:py-28">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <SectionHeader
          eyebrow="Section 03 · Wisdom Feed"
          title="Short teachings for the interior room."
          description="Mirror thoughts, essays, and sacred systems. Tap a teaching to enter the full room."
        />

        <ul className="mt-12 space-y-4">
          {items.map((w) => (
            <li
              key={w.slug}
              className="group relative rounded-2xl border hairline bg-obsidian/60 p-6 backdrop-blur transition-all hover:border-gold sm:p-8"
            >
              <Link
                to="/wisdom/$slug"
                params={{ slug: w.slug }}
                className="block"
              >
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
                  {w.category} · {w.readingTime}
                </p>
                <h3 className="mt-3 font-serif text-2xl group-hover:text-gold-luminous sm:text-3xl">
                  {w.title}
                </h3>
                <p className="mt-3 font-serif italic text-ivory/85">&ldquo;{w.pullQuote}&rdquo;</p>
                <p className="mt-4 text-sm text-muted-foreground">{w.excerpt}</p>
              </Link>

              <div className="mt-6 flex items-center gap-2 border-t hairline pt-4">
                <button
                  type="button"
                  onClick={() => toggleLike(w.slug)}
                  aria-pressed={!!liked[w.slug]}
                  aria-label={liked[w.slug] ? "Unlike" : "Like"}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                    liked[w.slug]
                      ? "border-gold bg-gold/10 text-gold-luminous"
                      : "hairline text-muted-foreground hover:border-gold hover:text-gold"
                  }`}
                >
                  <Heart size={12} className={liked[w.slug] ? "fill-current" : ""} />
                  {liked[w.slug] ? "Liked" : "Like"}
                </button>
                <button
                  type="button"
                  onClick={() => toggleSave(w.slug)}
                  aria-pressed={!!saved[w.slug]}
                  aria-label={saved[w.slug] ? "Unsave" : "Save"}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                    saved[w.slug]
                      ? "border-gold bg-gold/10 text-gold-luminous"
                      : "hairline text-muted-foreground hover:border-gold hover:text-gold"
                  }`}
                >
                  <Bookmark size={12} className={saved[w.slug] ? "fill-current" : ""} />
                  {saved[w.slug] ? "Saved" : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => share(w.slug, w.title)}
                  aria-label="Share teaching"
                  className="inline-flex items-center gap-1.5 rounded-full border hairline px-3 py-1.5 text-xs text-muted-foreground hover:border-gold hover:text-gold"
                >
                  <Share2 size={12} /> Share
                </button>
                <Link
                  to="/wisdom/$slug"
                  params={{ slug: w.slug }}
                  className="ml-auto inline-flex items-center gap-1 text-xs text-gold hover:text-gold-luminous"
                >
                  Read →
                </Link>
              </div>
            </li>
          ))}
        </ul>

        <div className="mt-10 flex flex-col items-center gap-3">
          {canLoadMore ? (
            <button
              type="button"
              onClick={() => setVisible((v) => Math.min(v + 3, source.length))}
              className="inline-flex items-center gap-2 rounded-full border border-gold px-6 py-3 text-sm text-gold hover:bg-gold hover:text-obsidian"
            >
              Continue the scroll
            </button>
          ) : (
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">End of the current transmission</p>
          )}
          <Link to="/wisdom" className="text-xs text-gold hover:text-gold-luminous">
            Enter the full library →
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ────────────────────── 5 · INNER TECHNOLOGIES ────────────────────── */
function InnerTechnologies() {
  const tech = [
    {
      icon: Compass,
      title: "Meditation",
      desc: "Stillness as architecture. Sittings that reshape the room the mind thinks in.",
      to: "/activations" as const,
    },
    {
      icon: Wind,
      title: "Breath",
      desc: "Rhythms — box, 4-7-8, coherent — used as levers on the nervous system.",
      to: "/activations" as const,
    },
    {
      icon: Eye,
      title: "Mirror Thinking",
      desc: "Trace the origin of a want. Ownership begins internally, then becomes paperwork.",
      to: "/wisdom" as const,
    },
    {
      icon: Shield,
      title: "Sacred Discipline",
      desc: "Softness with sovereignty. Structure is the choreography of freedom.",
      to: "/activations" as const,
    },
    {
      icon: Target,
      title: "Attention",
      desc: "The sacred currency. What you do not track, the world spends for you.",
      to: "/wisdom/$slug" as const,
      params: { slug: "attention-is-sacred-currency" },
    },
    {
      icon: AnkhMark,
      title: "Field Governance",
      desc: "Rule the interior field before you extend it outward. The temple before the temple gate.",
      to: "/wisdom/$slug" as const,
      params: { slug: "the-inner-world-is-where-ownership-begins" },
    },
  ] as const;

  return (
    <section id="technologies" data-reveal className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28">
      <SectionHeader
        eyebrow="Section 04 · The Inner Technologies"
        title="Six instruments of interior order."
        description="Practical mechanics — not vibes. Each carries its own protocol, sound, and lineage inside the ecosystem."
      />
      <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {tech.map((t) => {
          const Icon = t.icon;
          const inner = (
            <>
              <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full halo opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden />
              <div className="relative">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 bg-obsidian text-gold">
                  {t.title === "Field Governance" ? (
                    <AnkhMark size={22} />
                  ) : (
                    <Icon size={20} />
                  )}
                </div>
                <h3 className="mt-6 font-serif text-2xl">{t.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{t.desc}</p>
                <span className="mt-6 inline-flex items-center gap-1 text-xs text-gold">
                  Enter <ArrowRight size={12} />
                </span>
              </div>
            </>
          );
          return t.to === "/wisdom/$slug" ? (
            <Link
              key={t.title}
              to="/wisdom/$slug"
              params={t.params!}
              className="group relative overflow-hidden rounded-2xl border hairline bg-charcoal p-6 transition-all hover:border-gold hover:shadow-gold"
            >
              {inner}
            </Link>
          ) : (
            <Link
              key={t.title}
              to={t.to as "/activations" | "/wisdom"}
              className="group relative overflow-hidden rounded-2xl border hairline bg-charcoal p-6 transition-all hover:border-gold hover:shadow-gold"
            >
              {inner}
            </Link>
          );
        })}
      </div>
    </section>
  );
}

/* ────────────────────── 6 · BOOKS ────────────────────── */
function BooksSection() {
  const { books } = Route.useLoaderData() as { books: PublicBook[] };
  if (!books.length) return null;
  const featured = books.find((b) => b.featured) ?? books[0];
  const rest = books.filter((b) => b.slug !== featured.slug).slice(0, 3);
  return (
    <section id="books" data-reveal className="border-y hairline bg-charcoal py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeader
          eyebrow="Section 05 · Books"
          title="Instruction, in the hand."
          description="Working manuals for the reordered mind — passages to be revisited, not consumed."
        />

        {/* Featured Book */}
        <div className="mt-12 grid gap-10 rounded-2xl border hairline bg-obsidian/60 p-6 sm:p-10 md:grid-cols-[minmax(0,1fr)_1.2fr]">
          <Link
            to="/books/$slug"
            params={{ slug: featured.slug }}
            className="group block max-w-sm justify-self-center md:justify-self-start"
            aria-label={`${featured.title} — open book page`}
          >
            {featured.coverUrl ? (
              <div className="overflow-hidden rounded-lg border hairline bg-temple shadow-gold">
                <ResponsiveImage
                  src={featured.coverUrl}
                  alt={featured.coverAlt ?? `${featured.title} — cover art`}
                  sizes="(max-width: 768px) 92vw, 384px"
                  className="aspect-[3/4] h-auto w-full object-cover transition duration-500 group-hover:scale-[1.02]"
                />
              </div>
            ) : (
              <PlaceholderArt label={featured.title} aspect="portrait" />
            )}
          </Link>
          <div className="flex flex-col justify-center">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Featured Book</p>
            <h3 className="mt-3 font-serif text-3xl sm:text-4xl">{featured.title}</h3>
            {featured.author && (
              <p className="mt-2 text-sm text-muted-foreground">
                by <span className="text-ivory">{featured.author}{featured.authorLegalName ? ` (${featured.authorLegalName})` : ""}</span>
              </p>
            )}
            <p className="mt-3 max-w-xl font-serif italic text-ivory/85">&ldquo;{featured.subtitle}&rdquo;</p>
            <p className="mt-4 max-w-xl text-sm text-muted-foreground">{featured.longDescription}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/books/$slug"
                params={{ slug: featured.slug }}
                className="inline-flex items-center gap-2 rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian shadow-gold"
              >
                Open book page <ArrowRight size={14} />
              </Link>
              {featured.externalPurchaseUrl && (
                <a
                  href={featured.externalPurchaseUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-gold px-6 py-3 text-sm text-gold-luminous hover:bg-gold hover:text-obsidian"
                >
                  {featured.externalPurchaseLabel ?? "Read on Apple Books"} ↗
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {rest.map((b) => (
            <Link key={b.slug} to="/books/$slug" params={{ slug: b.slug }} className="group">
              {b.coverUrl ? (
                <div className="overflow-hidden rounded-lg border hairline bg-temple">
                  <ResponsiveImage
                    src={b.coverUrl}
                    alt={b.coverAlt ?? `${b.title} — cover art`}
                    sizes="(max-width: 768px) 92vw, 33vw"
                    className="aspect-[3/4] h-auto w-full object-cover transition duration-500 group-hover:scale-[1.02]"
                  />
                </div>
              ) : (
                <PlaceholderArt label={`${b.title}`} aspect="portrait" />
              )}
              <h3 className="mt-4 font-serif text-2xl group-hover:text-gold-luminous">{b.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{b.subtitle}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-xs text-gold">
                Read passage <ArrowRight size={12} />
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-10 flex justify-center">
          <Link
            to="/books"
            className="inline-flex items-center gap-2 rounded-full border border-gold px-6 py-3 text-sm text-gold hover:bg-gold hover:text-obsidian"
          >
            The full library <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ────────────────────── 7 · ACADEMY ────────────────────── */
function CoursesSection() {
  const tracks: Array<{ title: string; desc: string; duration: string; to: "/academy" | "/gates" | "/method" }> = [
    {
      title: "The Consciousness Architecture Academy",
      desc: "Long-form study paths across 14 schools — from decision architecture to the sutra cycle. University-depth lessons, guided practice, and journal work.",
      duration: "Ongoing · Guided study",
      to: "/academy",
    },
    {
      title: "The 47 Gates of Consciousness",
      desc: "Walk each Gate through reflection, breath, prompt, and journal. A signature practice arc — no leaderboards, no streaks, no currency.",
      duration: "47 Gates · At your own pace",
      to: "/gates",
    },
    {
      title: "The Method",
      desc: "The pillars of KAfrequency practice — breath, posture, attention, and the night seal — as a portable daily discipline.",
      duration: "Foundational · Self-guided",
      to: "/method",
    },
  ];
  return (
    <section id="academy" data-reveal className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28">
      <SectionHeader
        eyebrow="Section 06 · Academy & Practice"
        title="Guided arcs, not scattered lessons."
        description="Structured curricula for practitioners ready to build the interior from the studs."
      />
      <div className="mt-12 grid gap-4 md:grid-cols-3">
        {tracks.map((t) => (
          <Link
            key={t.title}
            to={t.to}
            className="group block rounded-2xl border hairline bg-charcoal p-8 transition-all hover:border-gold"
          >
            <GraduationCap size={22} className="text-gold" aria-hidden />
            <h3 className="mt-6 font-serif text-2xl group-hover:text-gold-luminous">{t.title}</h3>
            <p className="mt-3 text-sm text-muted-foreground">{t.desc}</p>
            <p className="mt-6 text-[10px] uppercase tracking-[0.3em] text-gold">{t.duration}</p>
            <span className="mt-4 inline-flex items-center gap-1 text-xs text-gold">
              Enter <ArrowRight size={12} />
            </span>
          </Link>
        ))}
      </div>
      <div className="mt-10 flex justify-center">
        <Link
          to="/academy"
          className="inline-flex items-center gap-2 rounded-full border border-gold px-6 py-3 text-sm text-gold hover:bg-gold hover:text-obsidian"
        >
          Enter the Academy <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
}


/* ────────────────────── 8 · COMMUNITY ────────────────────── */
function CommunitySection() {
  return (
    <section id="community" className="relative overflow-hidden border-y hairline bg-temple py-24 sm:py-32">
      <div className="absolute inset-0 -z-10 halo opacity-30" aria-hidden />
      <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Section 07 · Community</p>
        <h2 className="mt-6 font-serif text-4xl sm:text-5xl md:text-6xl">
          Do not only follow the frequency. <span className="bg-gold-gradient bg-clip-text text-transparent">Enter it.</span>
        </h2>
        <p className="mx-auto mt-6 max-w-2xl text-muted-foreground">
          A private field for members: early activations, unreleased music, creative notes, and live
          transmissions. Move from listener to practitioner.
        </p>
        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/membership"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold-gradient px-6 py-3.5 text-sm font-semibold text-obsidian shadow-gold hover:shadow-halo sm:w-auto"
          >
            <Users size={14} /> Join the Inner Circle
          </Link>
          <Link
            to="/community"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-gold px-6 py-3.5 text-sm text-gold hover:bg-gold hover:text-obsidian sm:w-auto"
          >
            Visit the commons
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ────────────────────── 9 · SHOP ────────────────────── */
function MerchSection() {
  return (
    <section id="shop" data-reveal className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
      <div className="rounded-2xl border hairline bg-charcoal p-8 sm:p-12">
        <div className="grid gap-8 md:grid-cols-[1.4fr_1fr] md:items-center">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Section 08 · Shop</p>
            <h2 className="mt-2 font-serif text-3xl sm:text-4xl">Objects that carry instruction.</h2>
            <p className="mt-4 max-w-xl text-muted-foreground">
              The KAfrequency shop carries the current inventory — books, digital releases, and vessels engineered
              to hold the practice. New drops are announced through the newsletter and the newsroom.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row md:flex-col">
            <Link
              to="/shop"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian shadow-gold"
            >
              <ShoppingBag size={14} /> Enter the shop
            </Link>
            <Link
              to="/books"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-gold px-6 py-3 text-sm text-gold hover:bg-gold hover:text-obsidian"
            >
              Books &amp; sutras <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}


/* ────────────────────── 9.5 · LATEST NEWS ────────────────────── */
function LatestNewsSection() {
  const latest = getNews().slice(0, 3);
  if (latest.length === 0) return null;
  return (
    <section id="news" data-reveal className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Official Newsroom</p>
          <h2 className="mt-2 font-serif text-3xl sm:text-4xl">Latest from Buddha KA</h2>
        </div>
        <Link to="/news" className="text-sm text-gold hover:text-gold-luminous">
          All Buddha KA news →
        </Link>
      </div>
      <ul className="mt-8 grid gap-4 sm:grid-cols-3">
        {latest.map((n) => (
          <li key={n.slug}>
            <Link
              to="/news/$slug"
              params={{ slug: n.slug }}
              className="group flex h-full flex-col rounded-2xl border hairline bg-charcoal p-5 transition-colors hover:border-gold"
            >
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
                {n.category} ·{" "}
                <time dateTime={n.publishedAt}>
                  {new Date(n.publishedAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" })}
                </time>
              </p>
              <h3 className="mt-3 font-serif text-lg leading-tight text-ivory group-hover:text-gold-luminous">
                {n.title}
              </h3>
              <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{n.dek}</p>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ────────────────────── 10 · NEWSLETTER ────────────────────── */
function NewsletterSection() {
  return (
    <section id="newsletter" className="border-y hairline bg-charcoal py-24">
      <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Section 09 · Newsletter</p>
        <h2 className="mt-4 font-serif text-4xl sm:text-5xl">Receive the Transmission.</h2>
        <p className="mt-4 text-muted-foreground">
          Music, activations, and writings — sent with intention, never noise.
        </p>
        <NewsletterForm source="home_bottom" />
        <ul className="mt-10 grid grid-cols-2 gap-2 sm:grid-cols-4" aria-label="Streaming platforms">
          {PLATFORMS.filter((p) => Boolean(STREAMING_LINKS[p])).map((p) => (
            <li key={p}>
              <a
                href={STREAMING_LINKS[p]}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center rounded-full border border-gold px-3 py-2 text-[11px] text-gold hover:bg-gold hover:text-obsidian"
              >
                {p}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function NewsletterForm({ source }: { source: string }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "success" | "duplicate" | "error">("idle");
  const [message, setMessage] = useState("");

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (state === "loading") return;
    setState("loading");
    setMessage("");
    try {
      const res = await subscribeEmail({ data: { email, source } });
      track("newsletter_submit", { source, duplicate: Boolean(res.duplicate) });
      if (res.duplicate) {
        setState("duplicate");
        setMessage(
          res.status === "confirmed"
            ? "You're already in the field."
            : "You're already on the list — check your inbox for the confirmation.",
        );
      } else {
        setState("success");
        setMessage("Check your inbox to confirm your subscription.");
        setEmail("");
      }
    } catch (err) {
      setState("error");
      setMessage(err instanceof Error && err.message ? err.message : "Please enter a valid email.");
    }
  }

  return (
    <>
      <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-3 sm:flex-row sm:gap-2" noValidate>
        <label htmlFor={`newsletter-${source}`} className="sr-only">
          Email
        </label>
        <input
          id={`newsletter-${source}`}
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@sacred.address"
          disabled={state === "loading"}
          aria-invalid={state === "error"}
          aria-describedby={message ? `newsletter-${source}-msg` : undefined}
          className="min-w-0 flex-1 rounded-full border hairline bg-obsidian px-5 py-3 placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-gold disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={state === "loading"}
          className="shrink-0 rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian shadow-gold disabled:opacity-60"
        >
          {state === "loading" ? "Sending…" : "Subscribe"}
        </button>
      </form>
      {message && (
        <p
          id={`newsletter-${source}-msg`}
          role={state === "error" ? "alert" : "status"}
          aria-live="polite"
          className={`mt-3 text-xs ${state === "error" ? "text-red-300" : "text-gold-luminous"}`}
        >
          {message}
        </p>
      )}
    </>
  );
}
